/**
 * The page's sign-in: the session token, the user it names and the active organization.
 *
 * The token lives in sessionStorage, so closing the tab signs it out. The active organization
 * lives in localStorage, because a member of several organizations rarely changes it within a
 * session.
 *
 * src/api/client.ts reads this state, so nothing here imports the api client; useSessionMe
 * hands in the /me answer.
 */

import { getSessionUser } from '@/api/sessionToken';
import type {
    AuthSnapshot, AuthStorage, AuthUser, MeResponse, SessionCookies,
} from '@/types/auth';
import type { ExternalStore, Unsubscribe } from '@/types/common';

const TOKEN_KEY = 'occurra.idToken';
const ORG_KEY = 'occurra.orgId';
// The backend sets this readable cookie beside the HttpOnly occurra_token cookie, with the same
// Max-Age, so that the page can see a live cookie session.
const SESSION_COOKIE = 'occurra_session_present';
const LOGOUT_URL = '/vms/api/v1/auth/logout';

type SessionFetch = (url: string, init: RequestInit) => Promise<unknown>;

interface AuthSessionDeps {
    /** Holds the token. */
    sessionStore: AuthStorage;
    /** Holds the active organization. */
    localStore: AuthStorage;
    cookies: SessionCookies;
    getSessionUser: (token: string) => AuthUser | null;
    /** Sends the sign-out request. */
    fetch: SessionFetch;
}

/** The sign-in state of the page, for React and for the transports that read it per request. */
export class AuthSession implements ExternalStore<AuthSnapshot> {
    private readonly sessionStore: AuthStorage;

    private readonly localStore: AuthStorage;

    private readonly cookies: SessionCookies;

    private readonly getSessionUser: (token: string) => AuthUser | null;

    private readonly fetch: SessionFetch;

    private token: string | null;

    private orgId: string | null;

    private user: AuthUser | null;

    private snapshot: AuthSnapshot | null = null;

    private readonly listeners = new Set<() => void>();

    constructor(deps: AuthSessionDeps) {
        this.sessionStore = deps.sessionStore;
        this.localStore = deps.localStore;
        this.cookies = deps.cookies;
        this.getSessionUser = deps.getSessionUser;
        this.fetch = deps.fetch;
        this.token = deps.sessionStore.getItem(TOKEN_KEY) || null;
        this.orgId = deps.localStore.getItem(ORG_KEY) || null;
        this.user = this.getTokenUser(this.token);
    }

    getToken(): string | null {
        return this.token;
    }

    setToken(token: string | null): void {
        this.token = token || null;
        this.user = this.getTokenUser(this.token);
        if (this.token === null) {
            this.sessionStore.removeItem(TOKEN_KEY);
        } else {
            this.sessionStore.setItem(TOKEN_KEY, this.token);
        }
        this.notify();
    }

    getActiveOrg(): string | null {
        return this.orgId;
    }

    /**
     * Prefer `switchActiveOrg` (auth/orgScope.ts): it drops the outgoing organization's cached
     * client state before it calls this. `reloadIntoOrg` does the same for `storeOrgForNextPage`.
     */
    setActiveOrg(orgId: string | null): void {
        this.orgId = orgId || null;
        this.storeOrgForNextPage(this.orgId);
        this.notify();
    }

    /**
     * For a page that reloads into another organization: a subscriber that heard of the change
     * would start the new organization's loads, and the reload cancels them.
     */
    storeOrgForNextPage(orgId: string | null): void {
        if (orgId === null) {
            this.localStore.removeItem(ORG_KEY);
        } else {
            this.localStore.setItem(ORG_KEY, orgId);
        }
    }

    getUser(): AuthUser | null {
        return this.user;
    }

    /** The sentinel says that the HttpOnly session cookie is still alive. */
    hasSessionCookie(): boolean {
        return this.cookies.read().split(';').some((part): boolean => {
            const [name, value] = part.trim().split('=');
            return name === SESSION_COOKIE && value === '1';
        });
    }

    /**
     * This page was signed in. It outlives the cookies, which expire together: a 401 then
     * means an expired session, not an anonymous probe at boot.
     */
    wasSignedIn(): boolean {
        return this.token !== null || this.user !== null || this.hasSessionCookie();
    }

    /**
     * A tab with only the cookie session has no token to take the display user from, so the
     * user comes from the /me answer. A sign-out expires the cookie and a sign-in sets a token,
     * so an answer that arrives after either one changes nothing.
     */
    setCookieSessionUser(me: MeResponse): void {
        if (this.token !== null || this.user !== null || !this.hasSessionCookie()) {
            return;
        }
        this.user = {
            email: me.email,
            name: '',
            picture: '',
        };
        this.notify();
    }

    /**
     * The user's own sign-out. The server revokes the HttpOnly cookie pair, which script cannot
     * reach: a cookie left alive could start the session again, for example through the
     * /local-login exchange. The client state clears at once, whatever the server answers.
     */
    logout(): void {
        this.fetch(LOGOUT_URL, {
            method: 'POST',
            credentials: 'include',
        }).catch((): void => {});
        this.clearSession();
    }

    /** The active organization stays, so that the next sign-in lands in it again. */
    clearSession(): void {
        if (this.hasSessionCookie()) {
            this.cookies.expireSentinel();
        }
        this.setToken(null);
    }

    getSnapshot(): AuthSnapshot {
        const isSignedIn = this.token !== null || this.hasSessionCookie();
        const previous = this.snapshot;
        if (previous !== null && previous.isSignedIn === isSignedIn &&
            previous.user === this.user && previous.orgId === this.orgId) {
            return previous;
        }
        this.snapshot = {
            isSignedIn,
            user: this.user,
            orgId: this.orgId,
        };
        return this.snapshot;
    }

    subscribe(listener: () => void): Unsubscribe {
        this.listeners.add(listener);
        return (): void => {
            this.listeners.delete(listener);
        };
    }

    private getTokenUser(token: string | null): AuthUser | null {
        return token === null ? null : this.getSessionUser(token);
    }

    private notify(): void {
        for (const listener of [...this.listeners]) {
            try {
                listener();
            } catch (error) {
                console.error('[auth] a listener failed', error);
            }
        }
    }
}

// The node tests load this module without a page.
const NO_STORAGE: AuthStorage = {
    getItem: (): null => null,
    setItem: (): void => {},
    removeItem: (): void => {},
};

function readCookies(): string {
    return typeof document === 'undefined' ? '' : document.cookie;
}

// A bare fetch, called as a method of another object, throws "Illegal invocation".
function sendRequest(url: string, init: RequestInit): Promise<Response> {
    return fetch(url, init);
}

function expireSentinelCookie(): void {
    if (typeof document !== 'undefined') {
        document.cookie = `${SESSION_COOKIE}=; Max-Age=0; path=/`;
    }
}

export const authSession = new AuthSession({
    sessionStore: typeof window === 'undefined' ? NO_STORAGE : window.sessionStorage,
    localStore: typeof window === 'undefined' ? NO_STORAGE : window.localStorage,
    cookies: {
        read: readCookies,
        expireSentinel: expireSentinelCookie,
    },
    getSessionUser,
    fetch: sendRequest,
});
